import { Helmet } from 'react-helmet-async';
import { Route, Routes, useLocation } from 'react-router';

import '../../initDayjs';

import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { usePageViewTracking } from '@client/hooks/useAnalytics';
import { useButtonClickGuard } from '@client/hooks/useButtonClickGuard';
import { useFcmToken } from '@client/hooks/useFcmToken';
import deviceStore from '@client/store/deviceStore';
import firebaseStore from '@client/store/firebaseStore';
import haewaoFirebaseConfig from './firebaseConfig';
import historyStore from '@client/store/historyStore';
import themeStore from '@client/store/themeStore';
import webViewStore from '@client/store/webViewStore';
import DailyBriefPage from './view/brief/DailyBriefPage';
import IndexHoldingsPage from './view/brief/IndexHoldingsPage';
import SectorHoldingsPage from './view/brief/SectorHoldingsPage';
import ChallengePage from './view/challenge/ChallengePage';
import ChannelPage from './view/channel/ChannelPage';
import PostEditorPage from './view/channel/PostEditorPage';
import PostPage from './view/channel/PostPage';
import FeaturedStockPage from './view/featured/FeaturedStockPage';
import Home from './view/home/HomePage';
import Layout from './view/Layout';
import AccountDeletePage from './view/more/account/AccountDeletePage';
import LicensePage from './view/more/license/LicensePage';
import MorePage from './view/more/MorePage';
import NewsDetailPage from './view/news/NewsDetailPage';
import NewsListPage from './view/news/NewsListPage';
import NotificationPage from './view/notification/NotificationPage';
import PortfolioDetailPage from './view/portfolio/detail/PortfolioDetailPage';
import PortfolioListPage from './view/portfolio/PortfolioListPage';
import ReportDetailPage from './view/portfolio/report/ReportDetailPage';
import SecurityDetailPage from './view/portfolio/security/SecurityDetailPage';
import StockDetailPage from './view/stock/StockDetailPage';
import StrategyCreatePage from './view/strategy/StrategyCreatePage';
import StrategyDetailPage from './view/strategy/StrategyDetailPage';
import StrategyEditPage from './view/strategy/StrategyEditPage';
import TermsPage from './view/terms/TermsPage';
import BridgeTestPage from './view/test/BridgeTestPage';
import DeepLinkTestPage from './view/test/DeepLinkTestPage';
import ReadabilityTestPage from './view/test/ReadbilityTestPage';

const App = observer(() => {
    const location = useLocation();

    useEffect(() => {
        historyStore.push(location);
    }, [location.pathname]);

    useEffect(() => {
        deviceStore.init();
        webViewStore.initialize();
        themeStore.initialize();

        // 순서 중요: webViewStore.initialize() 가 먼저 호출되어야 함
        firebaseStore.initialize(webViewStore.isWebView, haewaoFirebaseConfig);
    }, []);

    usePageViewTracking();
    useFcmToken();

    useButtonClickGuard();

    return (
        // @see React Router: https://reactrouter.com/start/library/routing
        <>
            <Helmet>
                <title>해와오 - 해외투자와 포트폴리오</title>
            </Helmet>
            <Routes>
                <Route element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="/portfolio" element={<PortfolioListPage />} />
                    <Route path="/portfolio/:institutionSlug" element={<PortfolioDetailPage />} />
                    <Route
                        path="/portfolio/:institutionSlug/report/:reportYear/:reportQuarter"
                        element={<ReportDetailPage />}
                    />
                    <Route
                        path="/portfolio/:institutionSlug/security/:securityHash"
                        element={<SecurityDetailPage />}
                    />
                    <Route path="/news" element={<NewsListPage />} />
                    <Route path="/news/:newsSummarySlug" element={<NewsDetailPage />} />
                    <Route path="/featured" element={<FeaturedStockPage />} />
                    <Route path="/daily-brief" element={<DailyBriefPage />} />
                    <Route
                        path="/daily-brief/sector/:sectorSlug"
                        element={<SectorHoldingsPage />}
                    />
                    <Route path="/daily-brief/index/:indexTicker" element={<IndexHoldingsPage />} />
                    <Route path="/challenge" element={<ChallengePage />} />
                    <Route path="/strategy/create" element={<StrategyCreatePage />} />
                    <Route path="/strategy/:hash" element={<StrategyDetailPage />} />
                    <Route path="/strategy/:hash/edit" element={<StrategyEditPage />} />
                    <Route path="/more" element={<MorePage />} />
                    <Route path="/notification" element={<NotificationPage />} />
                    <Route path="/terms" element={<TermsPage type="terms" />} />
                    <Route path="/privacy" element={<TermsPage type="privacy" />} />
                    <Route path="/more/license" element={<LicensePage />} />
                    <Route path="/more/account-delete" element={<AccountDeletePage />} />
                    <Route path="/channel/:channelType" element={<ChannelPage />} />
                    <Route path="/channel/:channelType/:postHash" element={<PostPage />} />
                    <Route path="/channel/:channelType/editor" element={<PostEditorPage />} />
                    <Route path="/stock/:ticker" element={<StockDetailPage />} />
                    {import.meta.env.DEV && (
                        <>
                            <Route path="/bridge-test" element={<BridgeTestPage />} />
                            <Route path="/deeplink-test" element={<DeepLinkTestPage />} />
                            <Route path="/readability-test" element={<ReadabilityTestPage />} />
                        </>
                    )}
                </Route>
            </Routes>
        </>
    );
});

export default App;
